import { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import ganttChartService from "../../../service/ganttChartService";
import {
  Employee,
  TransportationTask,
  TransportationType,
} from "../../../types/ganttChart";
import VerticalGanttChartTransportationIcon from "./VerticalGanttChartTransportationIcon";
import taskStyles from "../../../styles/ganttChart/tasks.module.css";
import { hideTaskHoverBox, showTaskHoverBox } from "../../../store/overlay";
import TaskViolationBox from "./TaskViolationBox";
import ViolationChip from "../../UI/chips/ViolationChip";
import { HighlightTasks } from "../../../utils/highlightUtil";
import { setHighlightedTasks } from "../../../store/search";
import labels from "../../../utils/labels";

const VerticalGanttChartTransportationTask = ({
  xPosition,
  yPosition,
  width,
  task,
  transportationType,
  employee,
}: {
  xPosition: number;
  yPosition: number;
  width: number;
  task: TransportationTask;
  transportationType: TransportationType;
  employee: Employee;
}) => {
  const dispatch = useAppDispatch();
  const { verticalColumnWidth } = useAppSelector(
    (state) => state.ganttChartSlice,
  );
  const highlightedTaskIds = useAppSelector(
    (state) => state.searchSlice.highlightedTaskIds,
  );
  const { isDragging } = useAppSelector((state) => state.dragAndDropSlice);
  const taskIsHovered = useAppSelector(
    (state) => state.overlaySlice.taskIsHovered,
  );
  const isSelected =
    taskIsHovered?.type === "TRANSPORTATION_TASK" &&
    taskIsHovered?.task.idTask === task.idTask;

  // See DraggableGanttChartBlock: clear the hover state on unmount if this task was the
  // current hover target, so a stale reference can't outlive this instance and keep matching
  // a later remount (e.g. after a reorder triggered by a neighboring task moving).
  const isSelectedRef = useRef(isSelected);
  isSelectedRef.current = isSelected;
  useEffect(() => {
    return () => {
      if (isSelectedRef.current) dispatch(hideTaskHoverBox());
    };
  }, [dispatch]);
  const { pixelPerMinute } = useAppSelector((state) => state.timelineSlice);
  const [taskHeight, setTaskHeight] = useState<number>(0);
  const iconHeight = 16;

  useEffect(() => {
    if (pixelPerMinute) {
      setTaskHeight(
        ganttChartService.timeDifferenceInMinutes(task.start, task.end) *
          pixelPerMinute,
      );
    }

    if (
      employee &&
      new Date(task.end).getTime() > new Date(employee?.shiftEndTime).getTime()
    ) {
      setTaskExceedsEndOfShift(true);
      const violationHeight =
        ganttChartService.timeDifferenceInMinutes(
          employee!.shiftEndTime,
          task.end,
        ) * pixelPerMinute;
      setViolationHeight(violationHeight);

      if (violationHeight >= taskHeight) {
        setFullTaskViolation(true);
      }
    } else {
      setTaskExceedsEndOfShift(false);
      setViolationHeight(undefined);
      setFullTaskViolation(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pixelPerMinute, task, employee]);

  /* Below are all the calculations to draw the red violation boxes, 
  when tasks exceed the employees shift. */
  const [taskExceedsEndOfShift, setTaskExceedsEndOfShift] =
    useState<boolean>(false);
  const [violationHeight, setViolationHeight] = useState<number>();
  const [fullTaskViolation, setFullTaskViolation] = useState<boolean>(false);

  return (
    <>
      {verticalColumnWidth && taskHeight && (
        <>
          <div
            style={{
              position: "absolute",
              left: xPosition + 2,
              top: yPosition,
              width,
              height: taskHeight,
            }}
            onMouseEnter={() => {
              // Suppress hover highlighting while a task is being dragged — the cursor
              // still passes over transport nodes mid-drag, which shouldn't light them up.
              if (isDragging) return;
              dispatch(
                showTaskHoverBox({
                  type: "TRANSPORTATION_TASK",
                  task: task,
                  employee: employee,
                }),
              );
            }}
            onMouseLeave={() => {
              dispatch(hideTaskHoverBox());
            }}
          >
            <div
              id={`TRANSPORTATION_TASK-${task.idTask}`}
              tabIndex={0}
              role="button"
              aria-label={`${labels.transportation}: ${employee.fullName}`}
              style={{
                height: taskHeight,
                width: width,
                opacity: HighlightTasks.getOpacity(
                  highlightedTaskIds,
                  task.idTask,
                ),
              }}
              className={`${taskStyles.transportationTask} ${isSelected ? taskStyles.taskSelected : ""}`}
              onClick={() => {
                dispatch(setHighlightedTasks([]));
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  dispatch(setHighlightedTasks([]));
                }
              }}
              onFocus={() => {
                dispatch(
                  showTaskHoverBox({
                    type: "TRANSPORTATION_TASK",
                    task: task,
                    employee: employee,
                  }),
                );
              }}
              onBlur={() => {
                dispatch(hideTaskHoverBox());
              }}
            >
              {iconHeight < taskHeight && (
                <VerticalGanttChartTransportationIcon
                  type={transportationType}
                />
              )}
              {task.violations && task.violations.length > 0 && (
                <ViolationChip />
              )}
            </div>
          </div>
          {taskExceedsEndOfShift && violationHeight && (
            <TaskViolationBox
              xPosition={xPosition}
              yPosition={yPosition}
              violationHeight={violationHeight}
              height={taskHeight}
              width={width}
              shiftEndTime={employee!.shiftEndTime}
              fullTaskViolation={fullTaskViolation}
            />
          )}
        </>
      )}
    </>
  );
};

export default VerticalGanttChartTransportationTask;
